import React from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MoreVertical } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface KebabMenuItem {
  label: string
  icon?: React.ReactNode
  onSelect: () => void
  /** Rose text + rose hover, per the sop-studio delete item. */
  destructive?: boolean
  disabled?: boolean
}

interface KebabMenuProps {
  items: KebabMenuItem[]
  /** Overrides the default light-surface trigger (e.g. for dark banners). */
  triggerClassName?: string
  align?: 'start' | 'end'
}

/**
 * Three-dot actions menu — DS pattern ported from sop-studio's SopDetailPage
 * kebab (white panel, slate items, rose destructive row).
 */
export const KebabMenu: React.FC<KebabMenuProps> = ({
  items,
  triggerClassName,
  align = 'end',
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <button
        onClick={(e) => e.stopPropagation()}
        aria-label="More actions"
        className={cn(
          'p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-md transition-colors',
          triggerClassName
        )}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
        className="w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden"
      >
        {items.map((item) => (
          <DropdownMenu.Item
            key={item.label}
            disabled={item.disabled}
            onSelect={item.onSelect}
            className={cn(
              'w-full px-3 py-2 text-sm flex items-center gap-2 cursor-pointer outline-none',
              'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
              item.destructive
                ? 'text-rose-600 data-[highlighted]:bg-rose-50'
                : 'text-slate-700 data-[highlighted]:bg-slate-100'
            )}
          >
            {item.icon}
            {item.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>
)

export default KebabMenu
